import React, { FC, Fragment, useCallback } from "react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CodeIcon from "@mui/icons-material/Code";
import { useAppAbility } from "@/services/permissions";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getSnowflakeConnectionCommandThunk } from "@/store/provider-connections/thunks/snowflake/getSnowflakeConnectionCommandThunk";
import { snowflakeConnectionCommandSelector } from "@/store/provider-connections/selectors/snowflake/snowflakeConnectionCommandSelector";
import { snowflakeConnectionQueryLoadingSelector } from "@/store/provider-connections/selectors/snowflake/snowflakeConnectionQueryLoadingSelector";
import { CommandSnippet } from "@/components/pages/common/connectors/components/CommandSnippet";
import { activeProviderConnectionIsCommandGeneratedSelector } from "@/store/provider-connections/selectors/active-connection/activeProviderConnectionIsCommandGeneratedSelector";
import { setActiveProviderConnectionIsCommandGenerated } from "@/store/provider-connections/providerConnectionsSlice";

export const SnowflakeConnectionDetailsGenerateCommand: FC = () => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotCreateIntegration = cannot("create", "integration");

  const command = useAppSelector(snowflakeConnectionCommandSelector);
  const loading = useAppSelector(snowflakeConnectionQueryLoadingSelector);
  const isCommandGenerated = useAppSelector(
    activeProviderConnectionIsCommandGeneratedSelector,
  );

  const generateCommandHandler = useCallback(() => {
    dispatch(getSnowflakeConnectionCommandThunk());
    dispatch(setActiveProviderConnectionIsCommandGenerated(true));
  }, [dispatch]);

  if (command && isCommandGenerated && !loading) {
    const commandLines = getCommandLineByLine(command);
    const formattedCommand = commandLines.map((line, index) =>
      line ? (
        <Typography
          sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
          variant="body2"
          key={index}
        >
          {line}
        </Typography>
      ) : (
        <br key={index} />
      ),
    );

    return (
      <Fragment>
        <Alert variant="outlined" severity="info">
          <AlertTitle>Make sure you have the right access!</AlertTitle>
          To run this setup script, your user account must have the
          <Typography fontWeight="bold" component="span">
            {" ACCOUNTADMIN "}
          </Typography>
          role. <br />
          If you're unsure whether you have this access, run: <br />
          <Typography fontWeight="medium">
            SHOW GRANTS TO USER {"<your_username>"}
          </Typography>
        </Alert>

        <CommandSnippet
          code={command}
          parentBoxSx={{ maxHeight: "45vh", overflow: "auto" }}
        >
          {formattedCommand}
        </CommandSnippet>
      </Fragment>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "grey.800",
        color: "white",
        px: 2,
        py: 1.5,
        borderRadius: 2,
        height: "100px",
        cursor: "default",
        userSelect: "none",
        position: "relative",
      }}
    >
      {/* Placeholder text for background */}
      <Typography variant="body2" maxWidth={400} sx={{ filter: "blur(8px)" }}>
        - Step 1: Create a new user and role for Cloudchipr integration USE ROLE
        ACCOUNTADMIN; <br />
        <Typography variant="body2" pt={1.5}>
          -- Create the Cloudchipr user if it doesn't exist, with RSA key
          authentication
        </Typography>
      </Typography>

      <LoadingButton
        loading={loading}
        onClick={generateCommandHandler}
        disabled={canNotCreateIntegration}
        loadingPosition="start"
        startIcon={
          <CodeIcon color={canNotCreateIntegration ? "disabled" : "primary"} />
        }
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          "&:hover": {
            bgcolor: "white",
          },
        }}
        variant="outlined"
      >
        Generate Command
      </LoadingButton>
    </Box>
  );
};

function getCommandLineByLine(commandString: string): string[] {
  return commandString.split("\n");
}
